import SkillDrawerButtons from 'components/feature/SkillDrawerButtons/SkillDrawerButtons';
import InfoItemGroup from 'components/molecules/InfoItemGroup/InfoItemGroup';
import DetailsCard from 'components/ui/DetailsCard/DetailsCard';
import { emptyDisplayValue } from 'constants/common';
import { Formik, useField } from 'formik';
import {
  CompanyRfuModelType,
  TechnicalSkillWithCompanyFragment,
  TechnicalSkillWithCompanyFragmentDoc,
  useTechnicalSkillRestoreMutation,
  useTechnicalSkillSoftDeleteMutation,
  useTechnicalSkillUpdateMutation,
} from 'generated/graphql';
import { useState } from 'react';
import { softSkillRFUModelTypeLabel } from 'views/StaticContent/SoftSkills/drawers/SoftSkillDrawer.helpers';
import {
  dataTestIds,
  EditTechnicalSkillFormValues,
  getInitialFormValues,
} from 'views/StaticContent/TechnicalSkills/EditTechnicalSkillDrawer/EditTechnicalSkillDrawer.helpers';
import { ApolloError, useApolloClient } from '@apollo/client';
import {
  Drawer,
  FormikTextInputField,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './EditTechnicalSkillDrawer.module.scss';

type EditTechnicalSkillDrawerProps = {
  open: boolean;
  onClose: () => void;
  technicalSkillSubId: string | null;
};

export function EditTechnicalSkillDrawer({
  open,
  onClose,
  technicalSkillSubId,
}: EditTechnicalSkillDrawerProps) {
  const client = useApolloClient();
  const technicalSkillLocal =
    client.readFragment<TechnicalSkillWithCompanyFragment>({
      fragment: TechnicalSkillWithCompanyFragmentDoc,
      id: client.cache.identify({
        __typename: 'TechnicalSkill',
        subId: technicalSkillSubId,
      }),
      fragmentName: 'TechnicalSkillWithCompany',
    });

  return (
    <Drawer
      open={open}
      title={`${technicalSkillLocal?.name || 'Technical Skill'}`}
      onClose={onClose}
    >
      {technicalSkillLocal ? (
        <DrawerContent technicalSkill={technicalSkillLocal} />
      ) : null}
    </Drawer>
  );
}

type DrawerContentProps = {
  technicalSkill: TechnicalSkillWithCompanyFragment;
};

function DrawerContent({ technicalSkill }: DrawerContentProps) {
  const { handleMsgType } = useNotification();
  const [isEditMode, setIsEditMode] = useState(false);

  const handleApiError = (error: ApolloError) => {
    handleMsgType({
      type: TNotification.error,
      message: error?.message,
      dismissable: true,
      time: 1500,
    });
  };

  const handleApiSuccess = (message: string) => {
    handleMsgType({
      type: TNotification.success,
      title: message,
      dismissable: true,
      time: 1500,
    });
  };

  const [softDeleteMutation, { loading: softDeleteLoading }] =
    useTechnicalSkillSoftDeleteMutation({
      variables: {
        subId: technicalSkill.subId,
      },
      onError: handleApiError,
      onCompleted: () => handleApiSuccess('Technical skill archived!'),
    });

  const [restoreMutation, { loading: restoreLoading }] =
    useTechnicalSkillRestoreMutation({
      variables: {
        subId: technicalSkill.subId,
      },
      onError: handleApiError,
      onCompleted: () => handleApiSuccess('Technical skill unarchived!'),
    });

  const [updateMutation, { loading: updateLoading }] =
    useTechnicalSkillUpdateMutation({
      onError: handleApiError,
      onCompleted: () => {
        handleApiSuccess('Technical skill updated!');
        setIsEditMode(false);
      },
    });

  const initialValues = getInitialFormValues(technicalSkill);

  const isArchived = Boolean(technicalSkill.deletedAt);

  const toggleArchive = () => {
    if (!isArchived) {
      softDeleteMutation();
    } else {
      restoreMutation();
    }
  };

  const handleSubmit = (formValues: EditTechnicalSkillFormValues) => {
    updateMutation({
      variables: {
        subId: technicalSkill.subId,
        args: {
          name: formValues.name,
          description: formValues.description,
        },
      },
    });
  };

  const detailsCardItems = [
    [
      {
        label: 'ID',
        value: initialValues?._id?.toString() ?? emptyDisplayValue,
      },
      {
        label: 'Category',
        value: technicalSkill.skillCategory?.name ?? emptyDisplayValue,
      },
    ],
    [
      {
        label: 'Slug',
        value: initialValues?.subId ?? emptyDisplayValue,
        hasCopyButton: true,
      },
      {
        label: 'Ownership',
        value: initialValues.ownership.label ?? emptyDisplayValue,
        valueNote:
          initialValues.ownership.label !== 'Global'
            ? `(${
                softSkillRFUModelTypeLabel[
                  technicalSkill?.company?.rfuModelType ||
                    CompanyRfuModelType.BehaviourBased
                ]
              })`
            : '',
      },
    ],
  ];
  const technicalSkillOverviewItems = [
    {
      label: 'Name',
      value: technicalSkill.name ?? emptyDisplayValue,
    },
    {
      label: 'Description',
      value: technicalSkill.description ?? emptyDisplayValue,
    },
  ];

  return (
    <Formik<EditTechnicalSkillFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ submitForm }) => (
        <form
          className={styles.form}
          onSubmit={e => e.preventDefault()}
          data-testid={dataTestIds.form}
        >
          <SkillDrawerButtons
            isEditMode={isEditMode}
            onArchive={toggleArchive}
            onArchiveLoading={softDeleteLoading || restoreLoading}
            isArchived={isArchived}
            onEdit={() => setIsEditMode(true)}
            onSave={submitForm}
            onSaveLoading={updateLoading}
            onCancel={() => setIsEditMode(false)}
          />
          <DetailsCard items={detailsCardItems} />
          {!isEditMode ? (
            <InfoItemGroup items={technicalSkillOverviewItems} />
          ) : (
            <div className={styles.editableFieldsContainer}>
              <FormikTextInputField
                id="name"
                label="Name"
                data-role="technical-skill-name-input"
                data-testid={dataTestIds.nameInput}
                useFormikField={useField}
                disabled={!isEditMode}
              />
              <FormikTextInputField
                id="description"
                label="Description"
                data-role="technical-skill-description-input"
                useFormikField={useField}
                placeholder="Missing description"
                disabled={!isEditMode}
                data-testid={dataTestIds.descriptionInput}
              />
            </div>
          )}
        </form>
      )}
    </Formik>
  );
}
